import { getInvoice } from '@/api';
import { InvoiceDetail, InvoiceWebResponse } from '@/components/Invoice/types';
import { CollectionMethod, InvoicePaymentStatus, InvoiceStatus } from '@/constants';
import { ErrorType } from '@/pages/Error';
import { airTracker } from '@/utils/airTracker';
import dayjs from 'dayjs';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import useQuery from './useQuery';
import { getTestClock } from '@/api/testClock';
import { COMPONENT_NAME } from '@/pages/Main';
import { useInterval } from 'ahooks';
import { sumDiscountAmounts } from '@/utils/discount';

const validateJwtToken = (jwtToken: any, history: any): boolean => {
  if (!jwtToken) {
    airTracker.error('apiError', {
      error: 'no jwt tok',
      api: 'getCheckout',
      component: COMPONENT_NAME,
    });
    history.push('/pay/error');
    return false;
  }

  if (jwtToken.exp < dayjs().unix()) {
    airTracker.error('apiError', {
      error: 'jwt tok expired',
      api: 'getCheckout',
      component: COMPONENT_NAME,
    });
    history.push(`/pay/error?code=${ErrorType.EXPIRED}`);
    return false;
  }

  return true;
};

const ensureBackwardCompatibility = (_data: InvoiceWebResponse): void => {
  // Placeholder for backward compatibility transformations if needed
};

const logInvoiceSuccess = (data: InvoiceWebResponse): void => {
  const {
    invoice,
    legalEntity,
    paymentSource,
    sellerId,
    shouldCreateMITConsent,
    linkedPaymentAccountCountryCode,
    creditNotes,
  } = data;
  const {
    id,
    billingEntityId,
    number,
    status,
    paymentStatus,
    createdAt,
    updatedAt,
    finalizedAt,
    dueAt,
    collectionMethod,
    emailInvoiceFinalized,
    subscriptionId,
    enableEmailDelivery,
    paidOutOfBand,
    lastPaymentAttemptAt,
    nextPaymentAttemptAt,
    pastPaymentAttemptCount,
    remainingPaymentAttemptCount,
    hostedInvoiceUrl,
    pdfUrl,
    amountRemaining,
    creditNoteAmountBeforePayment,
    creditNoteAmountAfterPayment,
    paymentsAmount,
    refundStatus,
    creditStatus,
    allPdfUrl,
  } = invoice;

  airTracker.updateCommonData({ sellerId });
  airTracker.info('fetchInvoiceSuccess', {
    component: COMPONENT_NAME,
    invoiceId: id,
    billingEntityId,
    number,
    status,
    paymentStatus,
    createdAt,
    updatedAt,
    finalizedAt,
    dueAt,
    collectionMethod,
    emailInvoiceFinalized,
    subscriptionId,
    enableEmailDelivery,
    paidOutOfBand,
    lastPaymentAttemptAt,
    nextPaymentAttemptAt,
    pastPaymentAttemptCount,
    remainingPaymentAttemptCount,
    hostedInvoiceUrl,
    pdfUrl,
    legalEntityName: legalEntity?.name,
    paymentSourceType: paymentSource?.type,
    sellerId,
    shouldCreateMITConsent,
    linkedPaymentAccountCountryCode,
    amountRemaining,
    creditNoteAmountBeforePayment,
    creditNoteAmountAfterPayment,
    paymentsAmount,
    refundStatus,
    creditStatus,
    allPdfUrl,
    creditNotes: JSON.stringify(creditNotes),
  });
};

export const useGetInvoice = (signature: string) => {
  const [loading, setLoading] = useState(true);
  const [invoiceData, setInvoiceData] = useState<InvoiceWebResponse>();
  const history = useHistory();

  const decodeJwt = useCallback(() => {
    try {
      return JSON.parse(atob(signature.split('.')[1]));
    } catch (e) {
      return null;
    }
  }, [signature]);

  const fetchInvoiceData = useCallback(async (): Promise<InvoiceWebResponse | null> => {
    try {
      const jwtToken = decodeJwt();
      if (!validateJwtToken(jwtToken, history)) {
        return null;
      }

      const response = await getInvoice(signature);
      const data = response.data;

      ensureBackwardCompatibility(data);
      logInvoiceSuccess(data);

      return data;
    } catch (error: any) {
      airTracker.error('getInvoice', {
        component: COMPONENT_NAME,
        error: error?.detail,
        detail: error?.detail?.error,
        type: 'fetch',
      });
      return null;
    }
  }, [signature, history, decodeJwt]);

  const shouldContinuePolling = useCallback((data: InvoiceWebResponse) => {
    if (data.invoice.collectionMethod === CollectionMethod.CHARGE_ON_CHECKOUT && data.invoice.totalDueAmount === 0) {
      return false;
    }
    return data.invoice.collectionMethod === CollectionMethod.CHARGE_ON_CHECKOUT && !data.transaction;
  }, []);

  const pollInvoice = useCallback(async () => {
    const data = await fetchInvoiceData();

    if (data && !shouldContinuePolling(data)) {
      setInvoiceData(data);
      setLoading(false);
    }
  }, [fetchInvoiceData, shouldContinuePolling]);

  useInterval(pollInvoice, loading ? 2000 : undefined, { immediate: true });

  return { invoiceData, loading };
};

export const useGetInvoiceCommonData = (invoiceData: InvoiceDetail) => {
  // remove this after testing
  const { testClockId, orgId } = useQuery<{
    testClockId: string;
    orgId: string;
  }>();
  const [dayNow, setDayNow] = useState(dayjs());

  useEffect(() => {
    if (testClockId && orgId) {
      getTestClock(testClockId, orgId).then((res) => {
        setDayNow(dayjs(res.now));
      });
    }
  }, [testClockId, orgId]);
  const {
    dueAt,
    collectionMethod,
    paymentStatus,
    lastPaymentAttemptAt,
    remainingPaymentAttemptCount,
    status,
    totalTaxAmount,
    totalDiscountAmount,
    discounts,
  } = invoiceData;
  const isSubscription = useMemo(() => !!invoiceData.subscriptionId, [invoiceData]);
  const overdue = useMemo(() => dayNow.isAfter(dayjs(dueAt)), [dueAt, dayNow]); // use dayjs after testing
  const autoCharge = useMemo(() => collectionMethod === CollectionMethod.AUTO_CHARGE, [collectionMethod]);
  const enabledPA = useMemo(() => collectionMethod !== CollectionMethod.OUT_OF_BAND, [collectionMethod]);
  const isPaid = useMemo(() => paymentStatus === InvoicePaymentStatus.PAID, [paymentStatus]);
  const isInDunning = useMemo(
    () => !!lastPaymentAttemptAt && !!remainingPaymentAttemptCount && remainingPaymentAttemptCount > 0,
    [lastPaymentAttemptAt, remainingPaymentAttemptCount]
  );
  const isDunningFailed = useMemo(
    () => !!lastPaymentAttemptAt && remainingPaymentAttemptCount === 0,
    [lastPaymentAttemptAt, remainingPaymentAttemptCount]
  );
  const isVoid = useMemo(() => status === InvoiceStatus.VOIDED, [status]);
  const hasTax = useMemo(() => totalTaxAmount !== undefined && totalTaxAmount > 0, [totalTaxAmount]);
  const hasDiscount = useMemo(
    () => totalDiscountAmount !== undefined && totalDiscountAmount > 0,
    [totalDiscountAmount]
  );
  const hasInvoiceDiscount = useMemo(() => sumDiscountAmounts(discounts) > 0, [discounts]);

  return {
    isSubscription,
    overdue,
    autoCharge,
    enabledPA,
    isPaid,
    isInDunning,
    isDunningFailed,
    isVoid,
    hasTax,
    hasDiscount,
    hasInvoiceDiscount,
  };
};
